import type { ConversationStatus, LeadStatus } from '@conversia/shared-types';
import type { CrmConfig } from '@/api/installation.api';
import { legible } from './cartera';

/**
 * Modelo de la ficha del lead — lógica PURA y testeable que la
 * `ContactDetailsPanel` cablea a los endpoints existentes. No hace fetch ni toca
 * el DOM: solo describe opciones, campos editables y las transiciones de estado
 * de las acciones rápidas, para que la UI quede delgada y el comportamiento se
 * pueda verificar con tests de funciones puras (entorno `node`, sin jsdom).
 *
 * Las opciones/labels se derivan de la config de la instalación (`CrmConfig`,
 * vía GET /api/installation-config/crm); las constantes por defecto reproducen
 * son neutras y sirven de fallback mientras la config carga.
 */

/** Opciones del dropdown de temperatura comercial (enum `LeadStatus`). */
export const LEAD_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'CALIENTE', label: 'Caliente' },
  { value: 'TIBIO', label: 'Tibio' },
  { value: 'FRIO', label: 'Frío' },
  { value: 'LARGO_PLAZO', label: 'Largo plazo' },
  { value: 'CERRADO', label: 'Cerrado' },
  { value: 'NO_CERRADO', label: 'No cerrado' },
];

/**
 * Opciones del dropdown de temperatura con los labels de la instalación. Los
 * valores (enum) son fijos; solo la etiqueta visible es configurable. PURO.
 */
export function leadStatusOptionsFrom(
  crm: CrmConfig | undefined,
): { value: LeadStatus; label: string }[] {
  if (!crm) return LEAD_STATUS_OPTIONS;
  return LEAD_STATUS_OPTIONS.map((o) => ({
    value: o.value,
    label: crm.leadStatusLabels[o.value] ?? o.label,
  }));
}

/**
 * Campos "detectados" que el humano puede corregir/llenar a mano. Las claves
 * coinciden con las que captura el agente en `profileDataJson` (ver
 * notion.service / lead-card). Se persisten con un merge no destructivo vía
 * `PATCH /api/contacts/:id` { profileData }.
 */
export interface EditableLeadField {
  key: string;
  label: string;
  placeholder: string;
  /** Tanda 6: con qué se edita. Sin `tipo` ni `opciones`, texto libre. */
  tipo?: 'texto' | 'numero';
  opciones?: string[];
}

export const EDITABLE_LEAD_FIELDS: EditableLeadField[] = [
  { key: 'viene_en_pareja', label: '¿Vienen en pareja?', placeholder: '', tipo: 'texto', opciones: ['pareja', 'solo'] },
  { key: 'que_les_pasa', label: 'Qué les pasa', placeholder: 'Con sus palabras, sin interpretar' },
  { key: 'tiempo_juntos', label: 'Tiempo juntos', placeholder: '3 años, casi un año…' },
  { key: 'ciudad', label: 'Ciudad', placeholder: 'Santo Domingo, Santiago…' },
  { key: 'entrada_que_mira', label: 'Entrada que mira', placeholder: '', tipo: 'texto', opciones: ['gold', 'platinum', 'diamond'] },
];

/** Campos editables de la ficha según la config de la instalación. PURO. */
export function editableLeadFieldsFrom(crm: CrmConfig | undefined): EditableLeadField[] {
  if (!crm || crm.editableFields.length === 0) return EDITABLE_LEAD_FIELDS;
  return crm.editableFields;
}

/**
 * Lo guardado en un campo de la ficha, como texto para su editor. Un número o un sí/no (los
 * escribe la IA o una importación) salen como cadena: leídos como vacío, el desplegable
 * mostraría «—» y tocarlo los pisaría sin que nadie lo viera (revisión de Codex). PURO.
 */
export function valorDeFicha(card: Record<string, unknown> | undefined, key: string): string {
  const v = card?.[key];
  return typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';
}

/**
 * Con qué se edita un campo de la ficha (tanda 6): desplegable si trae opciones, número si
 * es numérico, y si no, texto. Un valor viejo que no es número («unos 400k») se edita como
 * texto: un campo numérico lo mostraría vacío y no se podría ni leer ni corregir. PURO.
 */
export function editorDe(campo: Pick<EditableLeadField, 'tipo' | 'opciones'>, valor: string): 'opciones' | 'numero' | 'texto' {
  if (campo.opciones && campo.opciones.length > 0) return 'opciones';
  if (campo.tipo === 'numero' && (valor.trim() === '' || Number.isFinite(Number(valor)))) return 'numero';
  return 'texto';
}

/**
 * Las opciones del desplegable: «—» (vacío) primero, las configuradas con su nombre legible
 * («zoom_agendado» → «Zoom agendado») y, si lo guardado no es ninguna de ellas (lo escribieron
 * cuando el campo era texto libre), ese valor tal cual, para que no se pierda al abrir. PURO.
 */
export function opcionesDe(campo: Pick<EditableLeadField, 'opciones'>, valor: string): Array<{ value: string; label: string }> {
  const opciones = campo.opciones ?? [];
  return [
    { value: '', label: '—' },
    ...opciones.map((o) => ({ value: o, label: legible(o) })),
    ...(valor && !opciones.includes(valor) ? [{ value: valor, label: valor }] : []),
  ];
}

/** Claves que se editan arriba; se excluyen del bloque read-only para no duplicar. */
export function editableLeadKeysFrom(crm: CrmConfig | undefined): ReadonlySet<string> {
  return new Set(editableLeadFieldsFrom(crm).map((f) => f.key));
}

export const EDITABLE_LEAD_KEYS: ReadonlySet<string> = new Set(
  EDITABLE_LEAD_FIELDS.map((f) => f.key),
);

/** Clave donde viven las notas internas (single string) en `profileDataJson`. */
export const INTERNAL_NOTES_KEY = 'notas_internas';

/**
 * Clave reservada de `profileDataJson`: mapa { campo: ISO } con la fecha de la
 * última EDICIÓN MANUAL de cada campo (la sella el server en el PATCH del
 * panel). Sirve para mostrar "editado a mano" y no confundirlo con lo que
 * capturó la IA.
 */
export const MANUAL_EDITS_KEY = '_manual';

/** Mapa { campo: ISO } de ediciones manuales de la ficha, o {} si no hay. PURO. */
export function manualEditsFrom(card: Record<string, unknown> | undefined): Record<string, string> {
  const raw = card?.[MANUAL_EDITS_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
}

// "Agendar" YA NO es una transición de estado: abre el modal de crear trabajo
// (capa de operaciones, PASO 2) y se maneja aparte en la UI. Aquí quedan solo
// las acciones que mutan estado de la conversación/lead.
export type QuickAction = 'escalate' | 'discard';

/**
 * Transición de una acción rápida, en términos de los enums existentes:
 *  - `escalate` → conversación a WAITING_HUMAN (handoff a un humano), igual que
 *    "Tomar control"; opcionalmente se la asigna al operador actual.
 *  - `discard`  → lead perdido: temperatura NO_CERRADO + conversación CLOSED.
 */
export interface QuickActionTransition {
  action: QuickAction;
  label: string;
  toast: string;
  /** Nuevo `ConversationStatus`, si aplica. */
  status?: ConversationStatus;
  /** Nueva temperatura `LeadStatus`, si aplica. */
  leadStatus?: LeadStatus;
  /** Asignar la conversación al usuario actual (handoff). */
  assignToMe?: boolean;
}

export function quickActionTransition(action: QuickAction): QuickActionTransition {
  switch (action) {
    case 'escalate':
      return {
        action,
        label: 'Escalar',
        toast: 'Conversación escalada a un humano',
        status: 'WAITING_HUMAN',
        assignToMe: true,
      };
    case 'discard':
      return {
        action,
        label: 'Descartar',
        toast: 'Lead descartado',
        status: 'CLOSED',
        leadStatus: 'NO_CERRADO',
      };
  }
}

/**
 * Qué partes de la ficha del contacto se muestran. En un negocio con guion propio
 * (`crm.guion`, el mismo criterio que AjustesDelAgente) se ocultan las de otros
 * negocios: agendar trabajos de técnicos, ID interno y costo de API. Mientras la
 * config no llega (`undefined`) también se ocultan: así la ficha no las enseña un
 * instante antes de saber de quién es (revisión de Codex).
 */
export function seccionesVisibles(crm?: { guion?: unknown } | null): {
  agendar: boolean;
  idInterno: boolean;
  costoApi: boolean;
} {
  const otroNegocio = crm !== undefined && !crm?.guion;
  return { agendar: otroNegocio, idInterno: otroNegocio, costoApi: otroNegocio };
}
